
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus } from 'lucide-react';
import ListingCard from './ListingCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ListingsSectionProps {
  subscriptionActive: boolean;
  authLoading: boolean;
}

interface Listing {
  id: string;
  title: string;
  price: number | null;
  price_currency: string | null;
  breed: string;
  type: string;
  location: string | null;
  images: any;
  calving_ease: string | null;
  birth_weight: string | null;
  weaning_weight: string | null;
  milk: string | null;
  breeder_id: string;
  listing_type: string | null;
}

export const ListingsSection: React.FC<ListingsSectionProps> = ({ 
  subscriptionActive, 
  authLoading 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [breederName, setBreederName] = useState<string>('');

  useEffect(() => {
    if (user) {
      fetchListings();
      fetchBreederInfo();
    }
  }, [user]);

  const fetchBreederInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('business_name, first_name, last_name')
        .eq('id', user?.id)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setBreederName(data.business_name || `${data.first_name} ${data.last_name}`);
      }
    } catch (error) {
      console.error('Error fetching breeder info:', error);
    }
  };

  const fetchListings = async () => {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('breeder_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast({
        title: "Error",
        description: "Failed to load listings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (listingId: string) => {
    try {
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', listingId)
        .eq('breeder_id', user?.id);

      if (error) {
        throw error;
      }

      // Remove the deleted listing from local state
      setListings(prev => prev.filter(listing => listing.id !== listingId));
      
      toast({
        title: "Listing Deleted",
        description: "Your listing has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast({
        title: "Delete Failed",
        description: "There was an error deleting your listing. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatListingForCard = (listing: Listing) => {
    // Handle images - convert from Json to string array
    let imageArray: string[] = [];
    if (listing.images) {
      if (Array.isArray(listing.images)) {
        imageArray = listing.images;
      } else if (typeof listing.images === 'string') {
        try {
          imageArray = JSON.parse(listing.images);
        } catch {
          imageArray = [];
        }
      }
    }

    const imageUrl = imageArray && imageArray.length > 0 
      ? imageArray[0] 
      : "https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=300&h=200&fit=crop";

    return {
      id: listing.id, // Keep as string (UUID)
      title: listing.title,
      image: imageUrl,
      price: listing.price ? `${listing.price_currency || '$'} ${listing.price.toLocaleString()}` : undefined,
      location: listing.location || "Location not specified",
      breeder: breederName,
      breed: listing.breed,
      listing_type: listing.listing_type || undefined,
      evaluationData: {
        calvingEase: listing.calving_ease,
        birthWeight: listing.birth_weight,
        weaningWeight: listing.weaning_weight,
        milk: listing.milk,
      }
    };
  };

  const handleEditListing = (listingId: string) => {
    navigate(`/edit-listing/${listingId}`);
  };

  const bullListings = listings.filter(listing => listing.type.toLowerCase().includes('bull'));
  const cowListings = listings.filter(listing => listing.type.toLowerCase().includes('cow') || listing.type.toLowerCase().includes('heifer'));

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Your Listings</CardTitle>
          <Button 
            className="bg-green-700 hover:bg-green-800"
            disabled={!subscriptionActive}
            onClick={() => navigate('/add-listing')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Listing
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Your Listings</CardTitle>
        <Button 
          className="bg-green-700 hover:bg-green-800"
          disabled={!subscriptionActive}
          onClick={() => navigate('/add-listing')}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Listing
        </Button>
      </CardHeader>
      <CardContent>
        {!authLoading && subscriptionActive === false && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">
              Your subscription is inactive. Subscribe to add or edit listings.
            </p>
          </div>
        )}

        {listings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No listings yet. Create your first listing to get started!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {bullListings.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Bulls</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {bullListings.map((listing) => (
                    <ListingCard 
                      key={listing.id} 
                      listing={formatListingForCard(listing)} 
                      showEditButton={true}
                      onEdit={() => handleEditListing(listing.id)}
                      onDelete={() => handleDeleteListing(listing.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {cowListings.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Cows</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cowListings.map((listing) => (
                    <ListingCard 
                      key={listing.id} 
                      listing={formatListingForCard(listing)} 
                      showEditButton={true}
                      onEdit={() => handleEditListing(listing.id)}
                      onDelete={() => handleDeleteListing(listing.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
